import { createStyles, makeStyles } from '@material-ui/core';
import { theme } from '@saesa/ui-kit-front';
import { TipoColorLineaAvanceEnum } from 'config';
import { TAMANO_ICONO_CAMPANA_MODAL } from './outageDashboard.constants';

/**
 * Modal suscripción TER — Figma exacto
 * Desktop vacío: node 9146:50940
 * https://www.figma.com/design/K31x1F9r7zwsbgyU1ir7Gu/Mapa-de-cortes?node-id=9146-50940
 * Mobile: 9057:82017 (botones en columna Confirmar → Cerrar)
 *
 * Tokens:
 * - Espacio-500 padding 40 | Espacio-400 gap card 32 | Espacio-200 gap 16
 * - Radius XLarge 20 | Sombra B30 0 2 30 #09275424
 * - Title Body-L SemiBold 16/24 #074177
 * - Body Body-S Regular 14/20 #5A607F
 * - Input h45 r5 borde #9196B1 px16 placeholder #9196B1 14/18
 * - Botones SemiBold 14/18 r26 pt14 pb13 px32 | Disabled bg #EAEBF0 text #9196B1
 */
const ModalSuscripcionTerStyles = makeStyles(() =>
  createStyles({
    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(6, 7, 9, 0.45)',
      zIndex: 1300,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      boxSizing: 'border-box',
      [theme.breakpoints.down('sm')]: {
        padding: 16,
      },
    },
    /**
     * Card 412×559 (desktop) · p 40 · gap 32 · r 20 · sombra B30
     */
    card: {
      backgroundColor: '#FFFFFF',
      borderRadius: 20,
      boxShadow: '0px 2px 30px 0px rgba(9, 39, 84, 0.14)',
      padding: 40,
      width: 412,
      maxWidth: 'calc(100vw - 32px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 32,
      fontFamily: 'Neutrif Studio, sans-serif',
      boxSizing: 'border-box',
      maxHeight: 'calc(100vh - 48px)',
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: {
        /**
         * 9057:82017 — py 40 px 24, gap 32, full width
         */
        width: '100%',
        maxWidth: '100%',
        padding: '40px 24px',
        gap: 32,
        borderRadius: 20,
        maxHeight: 'calc(100vh - 32px)',
      },
    },
    /**
     * Bloque título: icon 24 + gap 16 + title + gap 16 + body
     */
    header: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
      width: '100%',
      flexShrink: 0,
    },
    iconoCampana: {
      width: TAMANO_ICONO_CAMPANA_MODAL,
      height: TAMANO_ICONO_CAMPANA_MODAL,
      display: 'block',
      flexShrink: 0,
    },
    /**
     * Body-L SemiBold 16/24 Azul-marino
     */
    titulo: {
      fontFamily: 'Neutrif Studio, sans-serif',
      fontWeight: 600,
      fontSize: 16,
      lineHeight: '24px',
      letterSpacing: 0,
      color: '#074177',
      margin: 0,
      padding: 0,
      textAlign: 'center',
      width: '100%',
    },
    /**
     * Body Figma: un bloque 14/20 #5A607F con línea vacía entre párrafos (~20px).
     * text-left (no centered).
     */
    textos: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      margin: 0,
      padding: 0,
    },
    texto: {
      fontFamily: 'Neutrif Studio, sans-serif',
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '20px',
      letterSpacing: 0,
      color: '#5A607F',
      margin: 0,
      padding: 0,
      textAlign: 'left',
      width: '100%',
    },
    /**
     * Línea en blanco Figma entre párrafos (Body-S 20px)
     */
    textoSpacer: {
      height: 20,
      width: '100%',
      flexShrink: 0,
      margin: 0,
      padding: 0,
      border: 0,
      display: 'block',
    },
    /**
     * Form ocupa gap del card: campos + botones son bloques del form con gap 32
     * (mismo spacing que en Figma entre sección inputs y sección botones).
     */
    form: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 32,
      width: '100%',
      margin: 0,
      padding: 0,
      position: 'relative',
    },
    /**
     * Sección inputs · gap 16 · full width
     */
    campos: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      width: '100%',
    },
    /**
     * Input: h45 r5 border 1 #9196B1 · px 16 · Párrafo 14/18
     */
    inputRoot: {
      width: '100%',
      margin: 0,
      '& .MuiOutlinedInput-root': {
        height: 45,
        minHeight: 45,
        borderRadius: 5,
        fontFamily: 'Neutrif Studio, sans-serif',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '18px',
        color: '#060709',
        backgroundColor: '#FFFFFF',
        boxSizing: 'border-box',
      },
      '& .MuiOutlinedInput-input': {
        padding: '13.5px 16px',
        height: 'auto',
        boxSizing: 'border-box',
        fontSize: 14,
        lineHeight: '18px',
        color: '#060709',
        '&::placeholder': {
          color: '#9196B1',
          opacity: 1,
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#9196B1',
        borderWidth: 1,
      },
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#9196B1',
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#4F1FFF',
        borderWidth: 1,
      },
      '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: TipoColorLineaAvanceEnum.IMPREVISTO,
      },
      /**
       * Label vacío = estado Figma placeholder interno #9196B1 14/18
       */
      '& .MuiInputLabel-outlined': {
        fontFamily: 'Neutrif Studio, sans-serif',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '18px',
        color: '#9196B1',
        transform: 'translate(16px, 13.5px) scale(1)',
      },
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        fontSize: 12,
        lineHeight: '16px',
        color: '#5A607F',
        backgroundColor: '#FFFFFF',
        padding: '0 5px',
        transform: 'translate(12px, -6px) scale(1)',
      },
      '& .MuiInputLabel-outlined.Mui-focused': {
        color: '#4F1FFF',
      },
      '& .MuiFormHelperText-root': {
        marginLeft: 2,
        marginRight: 0,
        fontFamily: 'Neutrif Studio, sans-serif',
        fontSize: 12,
        lineHeight: '16px',
      },
    },
    captchaWrap: {
      position: 'absolute',
      width: 1,
      height: 1,
      margin: -1,
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      border: 0,
      padding: 0,
    },
    captchaV2Wrap: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
    ocultarCaptcha: {
      display: 'none',
    },
    /**
     * Desktop 9146:50948: row gap 16, botones shrink al contenido, centrados
     * Mobile 9057:82025: column gap 16, full width, orden Confirmar → Cerrar
     */
    acciones: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'center',
      gap: 16,
      width: '100%',
      flexShrink: 0,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 16,
      },
    },
    /**
     * Outline · border 2 #4F1FFF · Párrafo-SemiBold 14/18 · r26 · pt14 pb13 px32
     */
    botonCerrar: {
      flex: '0 0 auto',
      backgroundColor: '#FFFFFF',
      border: '2px solid #4F1FFF',
      borderRadius: 26,
      padding: '14px 32px 13px',
      fontFamily: 'Neutrif Studio, sans-serif',
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '18px',
      letterSpacing: 0,
      color: '#4F1FFF',
      cursor: 'pointer',
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
      order: 1,
      '&:hover': {
        backgroundColor: '#F7F5FF',
      },
      [theme.breakpoints.down('sm')]: {
        order: 2,
        width: '100%',
      },
    },
    /**
     * Enabled: bg #4F1FFF text white
     * Disabled Figma: bg #EAEBF0 text #9196B1
     */
    botonConfirmar: {
      flex: '0 0 auto',
      backgroundColor: '#4F1FFF',
      border: '2px solid transparent',
      borderRadius: 26,
      padding: '14px 32px 13px',
      fontFamily: 'Neutrif Studio, sans-serif',
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '18px',
      letterSpacing: 0,
      color: '#FFFFFF',
      cursor: 'pointer',
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
      order: 2,
      '&:disabled': {
        backgroundColor: '#EAEBF0',
        color: '#9196B1',
        borderColor: 'transparent',
        cursor: 'not-allowed',
      },
      [theme.breakpoints.down('sm')]: {
        order: 1,
        width: '100%',
      },
    },
  })
);

export default ModalSuscripcionTerStyles;
